/**
 * Join-flow tenant override.
 *
 * When a logged-in member joins *another* community, the join request and
 * its (optional) onboarding must be addressed to the **target** tenant —
 * not the one the session is currently in — without switching the active
 * session/branding. This mirrors how the Angular member-portal embedded
 * the join + onboarding with an explicit `X-Tenant-ID` header.
 *
 * While an override is set, the request interceptor stamps `x-tenant-id`
 * with this id instead of the active tenant. Set it on entering the join
 * flow, clear it on leaving. Lives in a leaf module (no store imports) so
 * the interceptor can read it without a cycle.
 *
 * NB: this overrides the tenant *identity* only — the regional API base
 * stays the session's region. That is correct for single-region SSO
 * deployments (the common case); cross-region joins would additionally
 * need the target's regional base resolved.
 */
let joinTenantId: string | null = null;

/**
 * Bumped by every set. A screen releases its own claim by number, so a screen handing off to
 * another cannot clear an override the next one has already taken.
 *
 * Ordering alone cannot express this. The join screen clears on unmount, and React runs that
 * passive cleanup *after* the onboarding runner has rendered and after its layout effects — so
 * whichever way the runner re-asserted, the cleanup landed last and the first flow-instance
 * fetch went out addressed to the session's tenant.
 */
let claimGeneration = 0;

/** Take the override. The returned number identifies this claim for `releaseJoinTenantContext`. */
export function setJoinTenantContext(id: string | null | undefined): number {
  joinTenantId = (id ?? '').trim() || null;
  return ++claimGeneration;
}

/** Drop a claim. No-op once anything else has taken the override. */
export function releaseJoinTenantContext(claim: number): void {
  if (claim !== claimGeneration) return;
  joinTenantId = null;
}

export function getJoinTenantContext(): string | null {
  return joinTenantId;
}
